import { useQuery } from "@apollo/client";
import { Typography } from "@mui/material";
import { gql } from "generated-graphql";
import { TierIiReportStep } from "generated-graphql/graphql";
import { FacilityContacts } from "routes/Customer/Contacts/FacilityContacts";

import { ReportFloatingBar } from "../ReportFloatingBar";
import { StepperPageHeader } from "../StepperPageHeader";
import { useNavigateReportSaveCancel } from "../useNavigateReportSaveCancel";
import { useContactIssues, useFacilityContactIssues, useReport, GET_REPORT } from "../useReport";
import { useReportIsVerified } from "../useReportIsVerified";
import { useTouchReportMutation } from "../useTouchReportMutation";

const TIER_II_REPORT_CONTACTS = gql(`
  query TierIIReportContacts($reportId: ID!) {
    tierIIReportContacts(reportId: $reportId) {
      ...facilityContact
    }
  }
`);

export const ReportFacilityContacts = () => {
  const { data: report } = useReport();
  const isVerified = useReportIsVerified();
  const navigateToOverview = useNavigateReportSaveCancel();
  const allContactIssues = useContactIssues();
  const facilityContactIssues = useFacilityContactIssues();
  const { handleSave, loading: saveLoading } = useTouchReportMutation(
    report?.tierIIReport.id ?? "",
    report?.tierIIReport.touchedSteps ?? [],
    TierIiReportStep.Contacts
  );

  const reportId = report?.tierIIReport.id ?? "";
  const {
    data: contactsData,
    loading: contactsLoading,
    error: contactsError,
    previousData: contactsPreviousData,
  } = useQuery(TIER_II_REPORT_CONTACTS, {
    variables: { reportId },
    skip: !reportId,
    fetchPolicy: "cache-and-network",
  });

  const showInitialLoading = contactsLoading && !contactsPreviousData;

  const facilityContacts =
    contactsLoading || contactsError || !contactsData
      ? (contactsPreviousData?.tierIIReportContacts ?? [])
      : contactsData.tierIIReportContacts;

  return (
    <>
      <StepperPageHeader
        header="Contacts"
        mb={0}
        description={
          <>
            <Typography variant="body2">
              Wherever possible, Encamp has pre-populated your contact information based on what was
              reported in past years.
            </Typography>
            <Typography variant="body2">
              The agencies associated with this facility's location require contact information for
              specific roles associated with EPCRA.
            </Typography>
          </>
        }
      />
      <FacilityContacts
        loading={showInitialLoading}
        facilityId={report?.tierIIReport.facility.id ?? ""}
        facilityState={report?.tierIIReport.facility.state ?? ""}
        facilityContacts={facilityContacts}
        getRowIssues={(row) =>
          allContactIssues.filter(
            (issue) => issue.modelId === row.id || issue.modelId === row.person.id
          )
        }
        facilityContactIssues={facilityContactIssues}
        refetchQueries={[GET_REPORT, TIER_II_REPORT_CONTACTS]}
        isReadOnly={isVerified}
        excludeInternalRoles
      />
      {!isVerified && (
        <ReportFloatingBar
          loading={saveLoading}
          onSaveClick={handleSave}
          onCancel={navigateToOverview}
          issues={allContactIssues}
          saveDisabled={allContactIssues.length > 0}
          saveText="Mark as Complete"
        />
      )}
    </>
  );
};
